import { CRS, Direction, LatLngBoundsExpression, LayerOptions, Map as MapType, TooltipOptions } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImageOverlay, MapContainer } from 'react-leaflet';
import Marker from '../Element/Element';
import { EventClick, EventZoom } from '../Event/Event';
import style from "./Map.module.scss";
import { MapProps } from './types';


import { PushCategory } from '@ews/zlt-events';
import { useMyDetails } from '../../../Login/LoginProvider';
import { useNamedRoutes } from '../../../NamedRoutes';
import { usePersistentState } from '../../../Persistence';
import { SystemElement, useSystem, useSystemDetails } from '../../../ReactData/system';
import { isMainPlan as _isMainPlan } from '../../planUtils';
import { getEventColor } from '../Floors/Floors';
import { PlanButton } from '../PlanButton/PlanButton';


function getMinZoom(width: number, height: number, containerWidth: number = 1000, containerHeight: number = 1000)
{
    if (width > containerWidth || height > containerHeight)
        if (width > height) {
            return -Math.round(Math.pow(Math.round(width / containerWidth), 1));
        }
        else if (width < height) {
            return -Math.round(Math.pow(Math.round(height / containerHeight), 1));
        }
    return 0;
}

export function getScaleLevel(minZoom: number, currentZoom: number)
{
    return Math.round(minZoom < 0 ? currentZoom - minZoom : Math.abs(minZoom) + currentZoom);
}

const Map: React.FC<MapProps>
    = ({
        url,
        elementList = [],
        zoom = -2,
        onDeleteElement,
        onSetMarkerPosition,
        onSetLabelPosition,
        onEditElement,
        onAlignment,
        isMainPlan,
        planList = [],
        onDeletePlanButton,
        onEditPlanButton,
        events = {}
    }) =>
    {

        const { params } = useNamedRoutes();
        const myDetails = useMyDetails();
        const { id: systemId } = params();
        const [planEdit] = usePersistentState<boolean>("authorizationLevelPlan");

        const systemDetails = useSystemDetails(systemId || "");
        const system = useSystem(systemId || "");

        const [isLoaded, setIsLoaded] = useState<boolean>(false);
        const width = useRef<number>(0);
        const height = useRef<number>(0);
        const mapRef = useRef<MapType | null>(null);
        const [showLabel, setShowLabel] = useState<boolean>(true);
        const [_scale, setScale] = useState<number>(zoom);
        const [bounds, setBounds] = useState<LatLngBoundsExpression>([[0, 0]]);
        const [minZoom, setMinZoom] = useState<number>(0);
        const [selectedMarkers, setSelectedMarkers] = useState<SystemElement[]>([]);
        const markerList = useMemo(() => elementList, [elementList]);
        const imageRef = useRef<HTMLImageElement | null>(null);

        // console.log("Map.tsx: url", url ? url.length : url);

        const renderMap = useCallback(() =>
        {
            // console.log("Map.tsx: renderMap() was called", url ? url.length : url);
            setIsLoaded(true);

        }, [setIsLoaded]);

        useEffect(() =>
        {
            // console.log("Map.tsx: useEffect:", url);

            const img = new Image();
            img.src = url;
            img.onload = () =>
            {
                width.current = img.width;
                height.current = img.height;
                const bounds = [[0, 0], [img.height, img.width]] as LatLngBoundsExpression;
                const size = mapRef.current?.getSize() || { x: img.width, y: img.height }; //|| { x: 0, y: 0 };
                const minZoom = getMinZoom(img.width, img.height, size.x, size.y);
                mapRef.current?.setMinZoom(minZoom);

                mapRef.current?.once('zoomend', () => setScale(mapRef.current?.getZoom() || 0));
                mapRef.current?.fitBounds(bounds);

                setBounds(bounds);
                // setScale(getScaleLevel(minZoom, mapRef.current?.getZoom() || 0));
                //setZoom(minZoom);
                setMinZoom(minZoom);
                console.log("Map.tsx: useEffect: onload done, about to call renderMap", url ? url.length : url, size, bounds, minZoom, width, height, mapRef);

                imageRef.current = img;
                renderMap();
            };
            img.onabort = (...args) =>
            {
                console.error("Map.tsx: image loading was aborted!!!", url, args);
            };
            img.onerror = (...args) =>
            {
                console.error("Map.tsx: image loading errored!!!", url, args);
            };
            img.oncancel = (...args) =>
            {
                console.error("Map.tsx: image loading was canceled!!!", url, args);
            };
            img.onreset = (...args) =>
            {
                console.error("Map.tsx: image loading was reset!!!", url, args);
            };
        }, [url, minZoom, width, height, renderMap]);

        function handleClick(coords: { x: number, y: number; })
        {
            setSelectedMarkers([]);
            if (!planEdit) return;

            isMainPlan ?
                onSetLabelPosition(coords) :
                onSetMarkerPosition(coords);
        }

        function where(element1: SystemElement, element2: SystemElement)
        {
            return element1.groupId === element2.groupId! &&
                element1.id === element2.id! &&
                element1.numberGroup === element2.numberGroup;
        }

        function isSelectedMarker(element: SystemElement | null)
        {
            if (!element) return false;

            if (selectedMarkers.length === 0) return false;

            let selected = false;

            for (const _element of selectedMarkers) {

                if (where(element, _element)) {
                    selected = true;
                    break;
                }
            }
            return selected;
        }

        function findSelectedMarkerIndex(element: SystemElement)
        {
            return selectedMarkers.findIndex((marker) =>
            {
                return where(element, marker);

            });
        }

        function selectMarker(element: SystemElement)
        {
            setSelectedMarkers([...selectedMarkers, element]);
        }
        function unSelectMarker(element: SystemElement)
        {
            const index = findSelectedMarkerIndex(element);
            const markerList = selectedMarkers.filter((e, i) => i !== index);
            setSelectedMarkers(markerList);
        }
        const changeHandler = ({ direction, tooltipId }: { direction: Direction, tooltipId: number; }) =>
        {
            mapRef.current?.eachLayer(layer =>
            {
                if ("_container" in layer && (layer._container as HTMLDivElement).id.includes(`tooltip-${tooltipId}`)) {
                    (layer.options as LayerOptions & TooltipOptions).direction = direction;
                }
            });
        };

        // useCallback<React.RefCallback<LeafletMap>>((map) => {

        const handleMapRef = useCallback<React.RefCallback<any>>((map) =>
        {
            if (map) {
                // MOUNT
                console.log("Map.tsx: handleMapRef: Mounted:", map);
                mapRef.current = map;

                const img = imageRef.current;
                if (!img) {
                    console.error("Map.tsx: handleMapRef: imageRef.current is falsy during Map Mount! This should never happen!!!");
                    return;
                }

                width.current = img.width;
                height.current = img.height;
                const bounds = [[0, 0], [img.height, img.width]] as LatLngBoundsExpression;
                const size = mapRef.current?.getSize() || { x: img.width, y: img.height }; //|| { x: 0, y: 0 };
                const minZoom = getMinZoom(img.width, img.height, size.x, size.y);
                mapRef.current?.setMinZoom(minZoom);

                mapRef.current?.once('zoomend', () => setScale(mapRef.current?.getZoom() || 0));
                mapRef.current?.fitBounds(bounds);


            } else {
                // UNMOUNT
                console.log("Map.tsx: handleMapRef: Unmounted");
                mapRef.current = null;
            }
        }, []);

        const imageUrlLoaded = imageRef.current?.currentSrc || imageRef.current?.src;

        // CRS.Simple take the form of [y (height), x (width)] instead of [x, y], in the same way Leaflet uses [lat, lng] instead of [lng, lat].
        return ((isLoaded && imageUrlLoaded) ?
            <>
                {/* <IonItem style={{ width: "100%" }} lines='none'>
                    <IonButton
                        color={showLabel ? "danger" : "success"}
                        onClick={() => setShowLabel(!showLabel)}
                    >{showLabel ? t("[REACT_HIDE_LABEL]") : t("show Label")}</IonButton>
                </IonItem> */}
                <MapContainer
                    attributionControl={false}
                    className={`${style.leafletContainer}`}
                    zoomAnimation={false}
                    zoomSnap={0}
                    zoomDelta={0.125}
                    wheelPxPerZoomLevel={120}
                    markerZoomAnimation={false}
                    center={[
                        height.current / 2,
                        width.current / 2
                    ]}
                    ref={handleMapRef}
                    maxZoom={5}
                    /*   maxBounds={bounds}
                      maxBoundsViscosity={1}
                      bounceAtZoomLimits={false} */
                    doubleClickZoom={false}
                    crs={CRS.Simple}
                >
                    <ImageOverlay
                        url={imageUrlLoaded}
                        bounds={bounds}
                        alt='plan'
                        zIndex={10}
                    />
                    {
                        isMainPlan ?
                            planList.map(plan =>
                            {
                                const colorClassName = getEventColor(events[plan.id!] || PushCategory.None);

                                const { labelPositionX, labelPositionY, } = plan;

                                const _isMain = _isMainPlan(plan);
                                if (_isMain || (!labelPositionX || !labelPositionY)) return null;

                                return <PlanButton
                                    eventName={colorClassName}
                                    plan={plan}
                                    key={plan.id}
                                    planHeight={height.current}
                                    planWidth={width.current}
                                    zoom={_scale}
                                    mapHeight={height.current}
                                    mapWidth={width.current}
                                    onChange={(element, modified) =>
                                    {
                                        mapRef.current?.closePopup();
                                        onEditPlanButton && onEditPlanButton(element, modified);
                                    }}
                                    onDelete={(element) =>
                                    {
                                        onDeletePlanButton && onDeletePlanButton(element);
                                    }}
                                />;
                            })
                            :
                            markerList.map(([group, item], i) =>
                                <Marker
                                    key={i}
                                    defaultStyle={systemDetails.defaultPlanStyle}
                                    planHeight={height.current}
                                    planWidth={width.current}
                                    zoom={_scale}
                                    selected={isSelectedMarker(item)}
                                    element={item}
                                    group={group}
                                    selectedElements={selectedMarkers}
                                    allowedFlashEvents={myDetails.iconAnimation}
                                    hasFloodingZone={system.hasFloodingZone}
                                    showLabel={showLabel}
                                    mapHeight={height.current}
                                    mapWidth={width.current}
                                    onAlignment={(elements) =>
                                    {
                                        if (!planEdit) return;
                                        onAlignment(elements);
                                        setSelectedMarkers([]);
                                    }}
                                    onSelect={(selectedElement) =>
                                    {
                                        if (!planEdit) return;
                                        if (isSelectedMarker(selectedElement)) {
                                            unSelectMarker(selectedElement);

                                        } else {
                                            selectMarker(selectedElement);
                                        }
                                    }}
                                    onChange={(element, modified) =>
                                    {
                                        mapRef.current?.closePopup();
                                        //mapRef.current?.closeTooltip();
                                        onEditElement && onEditElement(element, modified);
                                    }}
                                    onDelete={(element) =>
                                    {
                                        onDeleteElement && onDeleteElement(element);
                                    }}
                                    onChangeTooltipDirection={changeHandler}
                                />
                            )
                    }
                    <EventClick onClick={handleClick} />
                    <EventZoom
                        onZoom={() =>
                        {
                            const zoom = mapRef.current?.getZoom();
                            setScale(zoom || 0);
                            /* const scale = getScaleLevel(minZoom, zoom || 0);
                            setScale(scale || 0); */
                        }} />
                </MapContainer >

            </>
            : null
        );
    };

export default Map;
